import React, { useState } from 'react';
import AppLayout from '@/components/app/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, CreditCard, Check } from 'lucide-react';
const AppBook = () => {
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const popularTreatments = [{
    id: 1,
    name: "AquaFirme Facial",
    credits: 4,
    duration: "30 min",
    description: "Deep hydration with vitamin infusion"
  }, {
    id: 2,
    name: "Botox",
    credits: 8,
    duration: "15 min",
    description: "Smooth fine lines and wrinkles"
  }, {
    id: 3,
    name: "Laser Whitening",
    credits: 6,
    duration: "45 min",
    description: "Professional teeth whitening"
  }];
  const availableDates = [{
    date: "Today",
    day: "Thu 6",
    available: false
  }, {
    date: "Tomorrow",
    day: "Fri 7",
    available: true
  }, {
    date: "Saturday",
    day: "Sat 8",
    available: true
  }, {
    date: "Monday",
    day: "Mon 10",
    available: true
  }, {
    date: "Tuesday",
    day: "Tue 11",
    available: true
  }];
  const availableTimes = ["9:00 AM", "10:30 AM", "12:00 PM", "1:30 PM", "3:00 PM", "4:30 PM", "6:00 PM"];
  return <AppLayout>
      <div className="px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-juv-deep-chestnut mb-2">Book Treatment</h1>
          <p className="text-gray-600">Schedule your next glow session</p>
        </div>

        {/* Step 1: Choose Treatment */}
        <Card className="bg-slate-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-juv-deep-chestnut">
              <div className="w-6 h-6 bg-juv-terracotta text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
              Choose Your Treatment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {popularTreatments.map(treatment => <div key={treatment.id} className={`p-4 border rounded-lg cursor-pointer transition-all ${selectedTreatment?.id === treatment.id ? 'border-juv-terracotta bg-juv-soft-blush' : 'border-gray-200 hover:border-juv-rose-clay'}`} onClick={() => setSelectedTreatment(treatment)}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-juv-deep-chestnut">{treatment.name}</h3>
                      <p className="text-sm text-gray-600 mb-1">{treatment.description}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-juv-terracotta font-medium">{treatment.credits} Credits</span>
                        <span className="text-gray-500">{treatment.duration}</span>
                      </div>
                    </div>
                    {selectedTreatment?.id === treatment.id && <Check className="w-5 h-5 text-juv-terracotta" />}
                  </div>
                </div>)}
            </div>
            
            <Button variant="outline" className="w-full mt-4">
              Browse All Treatments
            </Button>
          </CardContent>
        </Card>

        {/* Step 2: Select Date */}
        {selectedTreatment && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-juv-deep-chestnut">
                <div className="w-6 h-6 bg-juv-terracotta text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                Select Date
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                {availableDates.map((dateOption, index) => <div key={index} className={`p-3 text-center border rounded-lg cursor-pointer transition-all ${!dateOption.available ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' : selectedDate === dateOption.day ? 'border-juv-terracotta bg-juv-soft-blush text-juv-deep-chestnut' : 'border-gray-200 hover:border-juv-rose-clay'}`} onClick={() => dateOption.available && setSelectedDate(dateOption.day)}>
                    <div className="text-sm font-medium">{dateOption.date}</div>
                    <div className="text-xs">{dateOption.day}</div>
                  </div>)}
              </div>
            </CardContent>
          </Card>}

        {/* Step 3: Select Time */}
        {selectedDate && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-juv-deep-chestnut">
                <div className="w-6 h-6 bg-juv-terracotta text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                Select Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                {availableTimes.map((time, index) => <div key={index} className={`p-3 text-center border rounded-lg cursor-pointer transition-all ${selectedTime === time ? 'border-juv-terracotta bg-juv-soft-blush text-juv-deep-chestnut' : 'border-gray-200 hover:border-juv-rose-clay'}`} onClick={() => setSelectedTime(time)}>
                    <div className="text-sm font-medium">{time}</div>
                  </div>)}
              </div>
            </CardContent>
          </Card>}

        {/* Booking Summary & Confirmation */}
        {selectedTreatment && selectedDate && selectedTime && <Card className="bg-gradient-to-r from-juv-soft-blush to-juv-rose-clay">
            <CardHeader>
              <CardTitle className="text-juv-deep-chestnut">Booking Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-juv-terracotta" />
                  <div>
                    <p className="font-medium">{selectedTreatment.name}</p>
                    <p className="text-sm text-gray-600">{selectedTreatment.duration} treatment</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-juv-terracotta" />
                  <div>
                    <p className="font-medium">{selectedDate} at {selectedTime}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-juv-terracotta" />
                  <div>
                    <p className="font-medium">JUV Medspa - Downtown</p>
                    <p className="text-sm text-gray-600">123 Main St, Suite 100</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-juv-terracotta" />
                  <div>
                    <p className="font-medium">{selectedTreatment.credits} JUV Credits</p>
                    <p className="text-sm text-gray-600">No additional payment required</p>
                  </div>
                </div>
              </div>
              
              <Button className="w-full bg-juv-terracotta hover:bg-juv-deep-chestnut">
                Confirm Booking
              </Button>
              
              <p className="text-xs text-gray-600 mt-3 text-center">
                By booking, you agree to our cancellation policy. 
                Free cancellation up to 24 hours before your appointment.
              </p>
            </CardContent>
          </Card>}

        {/* Need Help? */}
        <Card>
          <CardContent className="p-4 text-center bg-slate-50">
            <h3 className="font-semibold text-juv-deep-chestnut mb-2">Need Help Choosing?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Our team can help you select the perfect treatment for your goals.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" size="sm" className="flex-1">
                Take Quiz
              </Button>
              <Button variant="outline" size="sm" className="flex-1">
                Call Studio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>;
};
export default AppBook;